/**
 * Admin V3 — Operations / Live — Dispatch table
 *
 * 7 columns: Priorité | Référence (+ anomaly badges) | Statut | Trajet
 *          | Date / Heure | Chauffeur | Actions.
 *
 * Row click → /admin/bookings/:id (legacy V2 detail page, owner ruling #5).
 *
 * NO mutation surface:
 *   - No cancel button
 *   - No reassign button
 *   - No incident-report button
 *   These are Phase 3+ scope.
 *
 * Visual reuse from visualTokens.js:
 *   - StatusPill (existing) — booking status
 *   - SmallPill (existing) — priority + anomaly badges
 *   - livePriorityMeta (new in Phase 2.5) — HIGH / MEDIUM / LOW
 *   - anomalyTypeMeta (new in Phase 2.5) — unassigned_30min / no_progress_60min
 *   - cn, shortAddress, EmptyState (existing)
 *
 * Defensive anomaly rendering: unknown anomaly types fall back to a neutral
 * slate pill displaying the raw key (owner ruling #4).
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import bookingDetailPath from '../bookings/bookingDetailPath';
import { ExternalLink, Activity, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  shortAddress,
  EmptyState,
  StatusPill,
  SmallPill,
  livePriorityMeta,
  anomalyTypeMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatDateTime = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(7).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// Defensive anomaly meta lookup with raw-key fallback (owner ruling #4).
const FALLBACK_ANOMALY_META_BASE = {
  pill: 'bg-slate-100 text-slate-500 border border-slate-200',
};
const anomalyMetaFor = (anomalyType) => {
  if (!anomalyType) return null;
  if (anomalyTypeMeta[anomalyType]) return anomalyTypeMeta[anomalyType];
  return { ...FALLBACK_ANOMALY_META_BASE, label: anomalyType };
};

const OperationsLiveTable = ({ bookings, anomalyByBookingId, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const navigate = useNavigate();

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-operations-live-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('operations.live.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-operations-live-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('operations.live.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Activity}
      title={t('operations.live.empty.title', locale)}
      desc={t(isFilterActive ? 'operations.live.empty.descFilter' : 'operations.live.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[
                t('operations.live.col.priority', locale),
                t('operations.live.col.reference', locale),
                t('operations.live.col.status', locale),
                t('operations.live.col.route', locale),
                t('operations.live.col.datetime', locale),
                t('operations.live.col.driver', locale),
                t('operations.live.col.actions', locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : bookings.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              bookings.map((b) => {
                const priorityKey = b.priority || 'LOW';
                const priorityMeta = livePriorityMeta[priorityKey] || livePriorityMeta.LOW;
                const refLabel = b.booking_reference || b.id?.substring(0, 8).toUpperCase() || '—';
                const detailPath = bookingDetailPath(b.id);
                const dateTime = b.scheduled_for || b.created_at || b.date || null;
                const rowAnomalies = anomalyByBookingId?.[b.id] || [];

                return (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                    {/* Priorité */}
                    <td className="px-4 py-3.5">
                      <SmallPill meta={priorityMeta} />
                    </td>

                    {/* Référence + anomaly badges */}
                    <td className="px-4 py-3.5">
                      <Link
                        to={detailPath}
                        className="inline-block rounded-lg bg-rose-50 px-2 py-1 font-mono text-[12px] font-bold text-rose-700 hover:bg-rose-100"
                        data-testid={`adminv3-operations-live-ref-${b.id}`}
                      >
                        {refLabel}
                      </Link>
                      {rowAnomalies.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {rowAnomalies.slice(0, 3).map((a, i) => {
                            const meta = anomalyMetaFor(a.type || a.anomaly_type || a);
                            if (!meta) return null;
                            return <SmallPill key={i} meta={meta} />;
                          })}
                        </div>
                      )}
                    </td>

                    {/* Statut */}
                    <td className="px-4 py-3.5">
                      <StatusPill status={b.status} />
                    </td>

                    {/* Trajet */}
                    <td className="px-4 py-3.5">
                      <div className="font-medium text-slate-900">{shortAddress(b.pickup_address, 24)}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        <span className="text-amber-500 mr-1">→</span>
                        {shortAddress(b.destination_address, 24)}
                      </div>
                    </td>

                    {/* Date / Heure */}
                    <td className="px-4 py-3.5 text-slate-500 text-[12px]">
                      {formatDateTime(dateTime, locale)}
                    </td>

                    {/* Chauffeur */}
                    <td className="px-4 py-3.5">
                      {b.assigned_driver_name ? (
                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-amber-400">
                            {initialsOf(b.assigned_driver_name)}
                          </div>
                          <span className="text-slate-700">{b.assigned_driver_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">{t('operations.live.driver.none', locale)}</span>
                      )}
                    </td>

                    {/* Actions (open only — NO cancel / reassign / incident) */}
                    <td className="px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(detailPath)}
                        className={cn(
                          'rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors'
                        )}
                        title={t('operations.live.action.open', locale)}
                        data-testid={`adminv3-operations-live-open-${b.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OperationsLiveTable;
